import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Select, MenuItem } from "@mui/material";
import {api}from "../services/api"; // Ensure this is correctly set up for API calls
import Layout from "../layout/layout";
import { useTheme } from "@mui/material/styles";


const ManagedDiskSelect = () => {
      const theme = useTheme();
    
  const [managedDisks, setManagedDisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDisk, setSelectedDisk] = useState("");
  const [redundancies, setRedundancies] = useState([]);
  const [selectedRedundancy, setSelectedRedundancy] = useState("");
  const [diskList, setDiskList] = useState([]);
  const [selectedMeter, setSelectedMeter] = useState("");
  const [diskPrice, setDiskPrice] = useState(null);

  useEffect(() => {
    const fetchManagedDisks = async () => {
      setLoading(true);
      try {
        const response = await api.get("/api/AzureResource/manageddisk -details", {
          headers: {
            "X-CORRELATION-ID": "7a0608ef-254b-49dd-af9d-06f2726d7acd",
          },
        });
        setManagedDisks(response.data);
      } catch (error) {
        console.error("Error fetching managed disks:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchManagedDisks();
  }, []);

  useEffect(() => {
    const fetchManagedDiskList = async () => {
      if (!selectedDisk || !selectedRedundancy) {
        return;
      }
      
      try {
        const managedDiskType = managedDisks.find(disk => disk.managedDiskId === selectedDisk)?.managedDiskTierName;
        const response = await api.get("/api/AzureResource/managed-disk-list", {
          headers: {
            "X-CORRELATION-ID": "7a0608ef-254b-49dd-af9d-06f2726d7acd",
          },
          params: {
            managedDiskType,
            Redundency: selectedRedundancy,
            templateId: 4, // Assuming templateId is fixed as 4
          },
        });
        setDiskList(response.data);
      } catch (error) {
        console.error("Error fetching managed disk list:", error);
      }
    };

    fetchManagedDiskList();
  }, [selectedDisk, selectedRedundancy, managedDisks]);

  useEffect(() => {
    const fetchDiskPrice = async () => {
      if (!selectedMeter) {
        return;
      }

      try {
        const response = await api.get(`/api/AzureResource/managed-disk-price/${selectedMeter}`, {
          headers: {
            "X-CORRELATION-ID": selectedMeter, // Assuming the correlation ID should match the disk ID
          },
        });
        setDiskPrice(response.data);
      } catch (error) {
        console.error("Error fetching disk price:", error);
      }
    };

    fetchDiskPrice();
  }, [selectedMeter]);

  const handleDiskSelectChange = (event) => {
    const managedDiskId = event.target.value;
    setSelectedDisk(managedDiskId);
    const selectedDiskData = managedDisks.find(disk => disk.managedDiskId === managedDiskId);
    setRedundancies(selectedDiskData?.redundancies || []);
    setSelectedRedundancy(""); // Reset redundancy selection when disk changes
    setDiskList([]); // Clear the disk list when disk selection changes
  };

  const handleRedundancySelectChange = (event) => {
    setSelectedRedundancy(event.target.value);
  };

  const handleMeterSelectChange = (event) => {
    setSelectedMeter(event.target.value);
  };

  return (
        <Layout pageType="sideBar">
             <Box
                    sx={{
                      flex: 1,
                      pl: 2,
                      pr: 2,
                      bgcolor: theme.palette.background.default,
                      color: theme.palette.text.primary,
                    }}
                  >
    <Typography variant="h5" gutterBottom sx={{mb:2}}>
              Managed Disk Price
            </Typography>
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Managed Disk Tier
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Select
            value={selectedDisk}
            onChange={handleDiskSelectChange}
            displayEmpty
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
          >
            <MenuItem value="" disabled>
              <em>Select a tier</em>
            </MenuItem>
            {managedDisks.map((disk) => (
              <MenuItem key={disk.managedDiskId} value={disk.managedDiskId}>
                {disk.managedDiskTierName}
              </MenuItem>
            ))}
          </Select>

          {selectedDisk && (
            <>
              <Typography variant="h6" gutterBottom>
                Select Redundancy Type
              </Typography>
              <Select
                value={selectedRedundancy}
                onChange={handleRedundancySelectChange}
                displayEmpty
                fullWidth
                variant="outlined"
                sx={{ mb: 2 }}
              >
                <MenuItem value="" disabled>
                  <em>Select a redundancy type</em>
                </MenuItem>
                {redundancies.map((redundancy) => (
                  <MenuItem key={redundancy.redundancyType} value={redundancy.redundancyType}>
                    {redundancy.displayName}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}

          {diskList.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Select Meter Name
              </Typography>
              <Select
                value={selectedMeter}
                onChange={handleMeterSelectChange}
                displayEmpty
                fullWidth
                variant="outlined"
              >
                <MenuItem value="" disabled>
                  <em>Select a meter</em>
                </MenuItem>
                {diskList.map((disk) => (
                  <MenuItem key={disk.pricingId} value={disk.pricingId}>
                    {disk.meterName}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}

          {diskPrice !== null && (
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Disk Price: ${diskPrice}
            </Typography>
          )}
        </>
      )}
    </Box>
    </Box>
    </Layout>
  );
};

export default ManagedDiskSelect;
